'use client'

import { useRef, useState } from 'react'

import { useMutation, useSubscription } from '@apollo/client'
import { Button } from '@canyon/ui/Button'
import { Dropzone } from '@canyon/ui/Dropzone'
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@canyon/ui/Form'
import { Input } from '@canyon/ui/Input'
import { MainShell } from '@canyon/ui/MainShell'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@canyon/ui/Select'
import { useToast } from '@canyon/ui/useToast'
import { useForm } from 'react-hook-form'
import Markdown from 'react-markdown'

import {
  RoastPayload,
  RoastResume_RoastResumeDocument,
  RoastRoleTypeEnum,
  RoastSubscriptionDocument,
  RoastToneLevelEnum,
} from '@gql/graphql'

import { uploadFile } from '@/utils/uploadFile'

const FORM_ID = 'roast-my-resume-form'

export default function RoastResume() {
  const { errorToast } = useToast()

  const [isFileUploading, setIsFileUploading] = useState<boolean>(false)
  const [fileSignedId, setFileSignedId] = useState<null | string>(null)
  const [_fileName, setFileName] = useState<string>('')
  const [isLoading, _setIsLoading] = useState(false)
  const [submitted, setSubmitted] = useState(false)

  const refRoastContentArray = useRef<RoastPayload[]>([])
  const [roastContent, setRoastContent] = useState('')
  const [roastRequestId, setRoastRequestId] = useState('')

  const [roastResume, { loading }] = useMutation(RoastResume_RoastResumeDocument)

  const defaultValues = {
    jobTitle: '',
    roleType: RoastRoleTypeEnum.Comedian,
    toneLevel: RoastToneLevelEnum.Humorous,
  }

  const roleTypeOptions: Record<RoastRoleTypeEnum, string> = {
    [RoastRoleTypeEnum.Boss]: 'Boss',
    [RoastRoleTypeEnum.Colleague]: 'Colleague',
    [RoastRoleTypeEnum.Comedian]: 'Comedian',
    [RoastRoleTypeEnum.Family]: 'Family',
    [RoastRoleTypeEnum.Friendly]: 'Friendly',
    [RoastRoleTypeEnum.Interviewer]: 'Interviewer',
    [RoastRoleTypeEnum.Mentor]: 'Mentor',
    [RoastRoleTypeEnum.Recruiter]: 'Recruiter',
    [RoastRoleTypeEnum.Spouse]: 'Spouse',
  }

  const toneLevelOptions: Record<RoastToneLevelEnum, string> = {
    [RoastToneLevelEnum.Dark]: 'Dark',
    [RoastToneLevelEnum.Direct]: 'Direct',
    [RoastToneLevelEnum.Friendly]: 'Friendly',
    [RoastToneLevelEnum.Humorous]: 'Humorous',
    [RoastToneLevelEnum.Informative]: 'Informative',
    [RoastToneLevelEnum.Inspirational]: 'Inspirational',
  }

  const form = useForm({
    defaultValues,
    values: { ...defaultValues },
  })

  const handleUploadResume = async (files: File[]) => {
    setIsFileUploading(true)

    const file = files[0]

    await uploadFile({
      file,
      onComplete: (response) => {
        setFileSignedId(response.signed_id)
        setFileName(response.filename)
        setIsFileUploading(false)
      },
      onError: () => {
        errorToast({ description: 'There was an error uploading your document.' })
        setIsFileUploading(false)
      },
    })
  }

  const onSubmit = async () => {
    const roleType = form.getValues().roleType
    const toneLevel = form.getValues().toneLevel
    if (!roleType || !toneLevel) {
      return
    }

    try {
      await roastResume({
        variables: {
          jobTitle: form.getValues().jobTitle,
          roleType,
          token:
            document.cookie.match('(^|;)\\s*' + 'roast-token' + '\\s*=\\s*([^;]+)')?.pop() || '',
          toneLevel,
          uploadSignedId: fileSignedId ?? '',
        },
      })
    } catch (e) {
      console.error(e)
    }
  }

  const compareSubscriptionData = (a: RoastPayload, b: RoastPayload) => {
    return (a.i || 0) - (b.i || 0)
  }

  useSubscription(RoastSubscriptionDocument, {
    onData: ({ data: { data } }) => {
      if (data?.roast?.initial == true) {
        setRoastContent('')
        setRoastRequestId(data?.roast?.requestId)
        setSubmitted(true)
      }
      if (data?.roast?.content && data?.roast.requestId == roastRequestId) {
        refRoastContentArray.current.push(data.roast)
        refRoastContentArray.current.sort(compareSubscriptionData)
        setRoastContent(refRoastContentArray.current.map((c) => c.content).join(''))
      }
    },
    variables: {},
  })

  return (
    <MainShell className="mx-auto mt-3 flex h-full w-[1000px] w-full max-w-screen-xl flex-col">
      <header className="mb-6 mt-3 flex flex-col gap-1 pl-6">
        <h1 className="flex items-center gap-2 text-2xl">🔥 Roast my Resume 🔥</h1>
        <p className="text-xl text-zinc-400">
          Upload your resume for candid, humorous feedback. To start, upload your resume and make
          your resume fireproof, one roast at a time.
        </p>
      </header>
      <Dropzone
        acceptsOnlyPdf={true}
        loading={isFileUploading || isLoading}
        onDropAccepted={handleUploadResume}
      />
      <Form {...form}>
        <form className="" id={FORM_ID} onSubmit={form.handleSubmit(onSubmit)}>
          <div className="flex flex-col space-y-4">
            <FormField
              control={form.control}
              name="roleType"
              render={({ field }) => (
                <FormItem className="w-full">
                  <FormLabel>Role Type</FormLabel>
                  <Select
                    onValueChange={(value) => {
                      if (value) {
                        field.onChange(value)
                      }
                    }}
                    {...field}
                  >
                    <FormControl defaultValue={RoastRoleTypeEnum.Comedian}>
                      <SelectTrigger>
                        <SelectValue placeholder="Select Role Type" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {Object.entries(roleTypeOptions).map((option) => (
                        <SelectItem key={option[0]} value={option[0]}>
                          {option[1]}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="toneLevel"
              render={({ field }) => (
                <FormItem className="w-full">
                  <FormLabel>Tone Level</FormLabel>
                  <Select
                    onValueChange={(value) => {
                      if (value) {
                        field.onChange(value)
                      }
                    }}
                    {...field}
                  >
                    <FormControl defaultValue={RoastToneLevelEnum.Humorous}>
                      <SelectTrigger>
                        <SelectValue placeholder="Select Tone Level" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {Object.entries(toneLevelOptions).map((option) => (
                        <SelectItem key={option[0]} value={option[0]}>
                          {option[1]}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="jobTitle"
              render={({ field }) => (
                <FormItem className="w-full">
                  <FormLabel>Job Title</FormLabel>
                  <FormControl>
                    <Input {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>
        </form>
      </Form>
      {!submitted && (
        <div className="flex flex-col">
          <Button
            className="mt-10 w-[400px] self-center"
            disabled={!fileSignedId || !form.getValues().jobTitle}
            form={FORM_ID}
            loading={loading}
            size="lg"
            type="submit"
            variant="cta"
          >
            Roast my Resume
          </Button>
        </div>
      )}
      <div className="mt-10">
        <Markdown
          components={{
            h1: ({ children }) => <h1 className="text-2xl font-bold">{children}</h1>,
            h2: ({ children }) => <h2 className="text-xl font-bold">{children}</h2>,
            p: ({ children }) => <p className="mb-2">{children}</p>,
          }}
        >
          {roastContent}
        </Markdown>
      </div>
    </MainShell>
  )
}
